import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import permissionManager from '../utils/permissionManager';
import secureStorage from '../utils/secureStorage';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Load saved credentials on component mount
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        const rememberMe = localStorage.getItem('remember_me') === 'true';
        
        if (rememberMe) {
          const savedEmail = await secureStorage.getItem('user_email');
          const savedPassword = await secureStorage.getItem('user_password');
          
          if (savedEmail && savedPassword) {
            setFormData(prev => ({
              ...prev,
              email: savedEmail,
              password: savedPassword,
              rememberMe: true
            }));
            console.log('Auto-filled saved credentials securely');
          } else {
            // Clear remember me flag if credentials are missing/expired
            localStorage.removeItem('remember_me');
          }
        }
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        clearSavedCredentials();
      }
    };

    loadCredentials();
  }, []);

  // Clear saved credentials
  const clearSavedCredentials = () => {
    localStorage.removeItem('remember_me');
    secureStorage.removeItem('user_email');
    secureStorage.removeItem('user_password');
  };

  // Validate form fields
  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'email':
        if (!value) {
          newErrors.email = 'Email is required';
        } else if (!emailRegex.test(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      case 'password':
        if (!value) {
          newErrors.password = 'Password is required';
        } else {
          delete newErrors.password;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: inputValue
    }));

    // Handle remember me checkbox changes
    if (name === 'rememberMe' && !inputValue) {
      // If unchecking remember me, clear saved credentials
      clearSavedCredentials();
    }

    // Validate field on change
    if (name !== 'rememberMe') {
      validateField(name, inputValue);
    }
  };

  // Check if form is valid
  useEffect(() => {
    const isEmailValid = formData.email && emailRegex.test(formData.email);
    const isPasswordValid = formData.password && formData.password.trim() !== '';
    const hasNoErrors = Object.keys(errors).length === 0;
    
    setIsFormValid(isEmailValid && isPasswordValid && hasNoErrors);
  }, [formData, errors]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    const isEmailValid = validateField('email', formData.email);
    const isPasswordValid = validateField('password', formData.password);
    
    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await apiService.login({
        email: formData.email,
        password: formData.password,
        remember_me: formData.rememberMe
      });

      const { data } = response;
      console.log({data});

      // Store tokens
      localStorage.setItem('access_token', data.tokens.access);
      localStorage.setItem('refresh_token', data.tokens.refresh);
      
      // Handle remember me functionality
      if (formData.rememberMe) {
        try {
          const emailSaved = await secureStorage.setItem('user_email', formData.email, 30);
          const passwordSaved = await secureStorage.setItem('user_password', formData.password, 30);
          
          if (emailSaved && passwordSaved) {
            localStorage.setItem('remember_me', 'true');
            console.log('Credentials saved securely with 30-day expiration');
          } else {
            console.error('Failed to save credentials securely');
          }
        } catch (error) {
          console.error('Error saving credentials:', error);
        }
      } else {
        clearSavedCredentials();
      }

      // Store user info and initialize permission manager
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        // Initialize permission manager with user data
        permissionManager.setUser(data.user);
      }

      // Redirect based on user permissions
      // Check if user can access dashboard, otherwise redirect to first accessible route
      if (permissionManager.hasPermission('view_dashboard')) {
        navigate('/dashboard');
      } else {
        // Find first accessible route from navigation items
        const navItems = permissionManager.getNavigationItems();
        const firstAccessibleRoute = navItems.find(item => item.path);
        if (firstAccessibleRoute) {
          navigate(firstAccessibleRoute.path);
        } else {
          // Fallback to dashboard if no specific routes are accessible
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        // Handle 400 error with non_field_errors (invalid email or password)
        if (errorData.errors?.non_field_errors) {
                  console.log({errorData})
          setErrors(prev => ({ 
            ...prev, 
            email: errorData.errors.non_field_errors[0] 
          }));
        }
        // Handle field-specific errors
        else if (errorData.email) {
          setErrors(prev => ({ ...prev, email: errorData.email[0] }));
        }
        else if (errorData.password) {
          setErrors(prev => ({ ...prev, password: errorData.password[0] }));
        }
        // Handle general errors
        else if (errorData.detail || errorData.non_field_errors) {
          setErrors(prev => ({ 
            ...prev, 
            general: errorData.detail || errorData.non_field_errors[0] 
          }));
        }
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/logo.webp" alt="Qualia Logo" className="logo-image" />
          </div>
          <p>Please sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="Enter your email"
              disabled={isLoading}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              <span className="checkmark"></span>
              Remember me
            </label>
          </div>

          <button
            type="submit"
            // className={`login-button ${!isFormValid || isLoading ? 'disabled' : ''}`}
            className={`login-button `}

            // disabled={!isFormValid || isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* <div className="login-footer">
          <a href="#" className="forgot-password">Forgot your password?</a>
        </div> */}
      </div>
    </div>
  );
};

export default Login;